import { Field, Select, Switch } from '~/components/workbench/tools/payments/ui';
import type { SettingsField, SettingsValues } from './marketplace-model';

/** The owner-facing settings form a plugin's manifest declares. Secrets are write-only. */
export function SettingsForm({
  fields,
  values,
  errors,
  secretsSet = [],
  onChange,
  disabled,
  showErrors,
}: {
  fields: readonly SettingsField[];
  values: SettingsValues;
  errors: Record<string, string>;
  secretsSet?: readonly string[];
  onChange: (next: SettingsValues) => void;
  disabled?: boolean;
  /** Show field errors (after a first submit attempt or once touched). */
  showErrors?: boolean;
}) {
  const set = (key: string, value: string | boolean) => onChange({ ...values, [key]: value });

  return (
    <div className="space-y-3">
      {fields.map((f) => {
        const error = showErrors ? errors[f.key] : undefined;
        const hint = error ? <span className="text-danger">{error}</span> : f.help;
        const label = f.required ? `${f.label} *` : f.label;

        if (f.type === 'boolean') {
          return (
            <div key={f.key} className="space-y-1">
              <Switch
                checked={values[f.key] === true}
                onChange={(v) => set(f.key, v)}
                label={f.label}
                disabled={disabled}
              />
              {f.help && <p className="text-[11px] leading-relaxed text-muted">{f.help}</p>}
            </div>
          );
        }

        if (f.type === 'select') {
          return (
            <Field key={f.key} label={label} hint={hint}>
              <Select
                value={String(values[f.key] ?? '')}
                onChange={(v) => set(f.key, v)}
                label={f.label}
                disabled={disabled}
              >
                <option value="">Choose…</option>
                {(f.options ?? []).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
          );
        }

        const secretOnFile = f.type === 'secret' && secretsSet.includes(f.key);

        return (
          <Field key={f.key} label={label} hint={hint}>
            <input
              type={
                f.type === 'secret'
                  ? 'password'
                  : f.type === 'number'
                    ? 'number'
                    : f.type === 'email'
                      ? 'email'
                      : f.type === 'url'
                        ? 'url'
                        : 'text'
              }
              value={String(values[f.key] ?? '')}
              onChange={(e) => set(f.key, e.target.value)}
              placeholder={secretOnFile ? 'Saved. Type to replace it.' : f.placeholder}
              autoComplete={f.type === 'secret' ? 'new-password' : 'off'}
              disabled={disabled}
              aria-invalid={Boolean(error)}
              className={f.type === 'secret' ? 'ui-input-mono' : 'ui-input'}
            />
          </Field>
        );
      })}
    </div>
  );
}
